import { Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, TextField, Typography } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import {
    Add as AddIcon,
    Badge as BadgeIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    CloudDownload as CloudDownloadIcon,
    CloudUpload as CloudUploadIcon,
    Close as CloseIcon,
} from "@mui/icons-material"
import { useCallback, useEffect, useState } from "react"
import { useAppSelector } from "@/store"
import { useDebounce } from "@/hooks/useDebounce"

import { membershipApi } from "@/services/api"
import { format as formatDateFns } from 'date-fns'
import toast from "react-hot-toast"
import { setLoading } from "@/store/slices/authSlice"
import { Loader } from "lucide-react"

interface BoardInfo {
    _id: string
    name: string
}


interface License {
    _id: string
    therapistId: string
    licenseState: string
    licenseNumber: string
    licenseType: string
    licenseNpi: string
    boardName?: string | BoardInfo
    boardSpeciality?: string | BoardInfo
    boardSubSpeciality?: string | BoardInfo
    boardCertificationNo?: string
    issueDate?: string
    expiryDate: string
    licenseStatus: string
    licenseFileUrl: string | string[]
    licenseFileUrl1?: string
    licenseFileUrl2?: string
    licenseFileUrl3?: string
    licenseFileUrl4?: string
    licenseFileUrls?: string[]
    file?: any[] // Support multiple files if returned as 'files'
    isDeleted: boolean
    createdAt: string
    updatedAt: string
}
interface LicenseType {
    _id: string
    name: string
    description: string
    isDeleted: boolean
    required?: 'mandatory' | 'non_mandatory'
    task: string
}

interface LicensesResponse {
    message: string
    currentPage: string
    totalRecords: number
    totalPages: number
    limit: string
    data: License[]
}


const LicenseManagementPage: React.FC = () => {
    const { user } = useAppSelector(state => state.auth)
    const [addLicenseDialogOpen, setAddLicenseDialogOpen] = useState(false)
    const [isLoadingLicenses, setIsLoadingLicenses] = useState(false)
    const [licenses, setLicenses] = useState<License[]>([])
    const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([])
    const [editingLicenseId, setEditingLicenseId] = useState<string | null>(null)
    const [licenseFile, setLicenseFile] = useState<Map<string, File[]>>(new Map())
    const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [isLoadingLicenseTypes, setIsLoadingLicenseTypes] = useState(false)
    const [states, setStates] = useState<Array<{ name: string; code: string }>>(
        []
    )
    const [newLicense, setNewLicense] = useState<Partial<License>>({
        licenseType: '',
        licenseNpi: '',
        licenseState: '',
        licenseNumber: '',
        issueDate: '',
        expiryDate: '',
        licenseStatus: 'Active',
        licenseFileUrl: '',
        boardName: '',
        boardSpeciality: '',
        boardSubSpeciality: '',
    })
    const [newLicenseFiles, setNewLicenseFiles] = useState<File[]>([])
    const handleOpenAddLicenseDialog = () => {
        setAddLicenseDialogOpen(true)
    }

    const handleCloseAddLicenseDialog = () => {
        setAddLicenseDialogOpen(false)
        // Clean up preview URL if it exists
        if (
            typeof newLicense.licenseFileUrl === 'string' &&
            newLicense.licenseFileUrl.startsWith('blob:')
        ) {
            URL.revokeObjectURL(newLicense.licenseFileUrl)
        }
        // Reset form
        setNewLicense({
            licenseType: '',
            licenseNpi: '',
            licenseState: '',
            licenseNumber: '',
            issueDate: '',
            expiryDate: '',
            licenseStatus: 'Active',
            licenseFileUrl: '',
            boardName: '',
            boardSpeciality: '',
            boardSubSpeciality: '',
            boardCertificationNo: '',
        })
        setNewLicenseFiles([])
    }

    // Board cascading search state
    const [boardNameSearch, setBoardNameSearch] = useState('')
    const [boardSpecialitySearch, setBoardSpecialitySearch] = useState('')
    const [boardSubSpecialitySearch, setBoardSubSpecialitySearch] = useState('')

    const [boardNameOptions, setBoardNameOptions] = useState<BoardInfo[]>([])
    const [boardSpecialityOptions, setBoardSpecialityOptions] = useState<BoardInfo[]>([])
    const [boardSubSpecialityOptions, setBoardSubSpecialityOptions] = useState<BoardInfo[]>([])

    const [isSearchingBoardName, setIsSearchingBoardName] = useState(false)
    const [isSearchingBoardSpeciality, setIsSearchingBoardSpeciality] = useState(false)
    const [isSearchingBoardSubSpeciality, setIsSearchingBoardSubSpeciality] = useState(false)

    const debouncedBoardNameSearch = useDebounce(boardNameSearch, 300)
    const debouncedBoardSpecialitySearch = useDebounce(boardSpecialitySearch, 300)
    const debouncedBoardSubSpecialitySearch = useDebounce(boardSubSpecialitySearch, 300)
    const [addLoading, setAddLoading] = useState(false)
    // Initial fetch for board names
    useEffect(() => {
        const fetchInitialBoardNames = async () => {
            setIsSearchingBoardName(true)
            try {
                const response = await membershipApi.getBoardNames('')
                const data = response?.data || response
                setBoardNameOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board names:', error)
            } finally {
                setIsSearchingBoardName(false)
            }
        }
        fetchInitialBoardNames()
    }, [])

    // Search board options when debounced input changes
    useEffect(() => {
        const searchBoardNames = async () => {
            setIsSearchingBoardName(true)
            try {
                const response = await membershipApi.getBoardNames(
                    debouncedBoardNameSearch
                )
                const data = response?.data || response
                setBoardNameOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board names:', error)
                setBoardNameOptions([])
            } finally {
                setIsSearchingBoardName(false)
            }
        }
        searchBoardNames()
    }, [debouncedBoardNameSearch])

    const activeBoardId = (() => {
        const val = addLicenseDialogOpen
            ? newLicense.boardName
            : licenses.find(l => l._id === editingLicenseId)?.boardName
        return typeof val === 'object' ? val._id : (val || '')
    })()

    const activeSpecialityId = (() => {
        const val = addLicenseDialogOpen
            ? newLicense.boardSpeciality
            : licenses.find(l => l._id === editingLicenseId)?.boardSpeciality
        return typeof val === 'object' ? val._id : (val || '')
    })()

    useEffect(() => {
        const searchBoardSpecialities = async () => {
            if (!activeBoardId) {
                setBoardSpecialityOptions([])
                return
            }
            setIsSearchingBoardSpeciality(true)
            try {
                // Use non-debounced search if empty to ensure immediate fetch on parent change
                const searchTerm = boardSpecialitySearch === '' ? '' : debouncedBoardSpecialitySearch
                const response = await membershipApi.getBoardSpecialities(
                    searchTerm,
                    activeBoardId
                )
                const data = response?.data || response
                setBoardSpecialityOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board specialities:', error)
                setBoardSpecialityOptions([])
            } finally {
                setIsSearchingBoardSpeciality(false)
            }
        }
        searchBoardSpecialities()
    }, [debouncedBoardSpecialitySearch, activeBoardId])

    useEffect(() => {
        const searchBoardSubSpecialities = async () => {
            if (!activeSpecialityId) {
                setBoardSubSpecialityOptions([])
                return
            }
            setIsSearchingBoardSubSpeciality(true)
            try {
                // Use non-debounced search if empty to ensure immediate fetch on parent change
                const searchTerm = boardSubSpecialitySearch === '' ? '' : debouncedBoardSubSpecialitySearch
                const response = await membershipApi.getBoardSubspecialities(
                    searchTerm,
                    activeSpecialityId
                )
                const data = response?.data || response
                setBoardSubSpecialityOptions(Array.isArray(data) ? data : [])
            } catch (error) {
                console.error('Error fetching board subspecialities:', error)
                setBoardSubSpecialityOptions([])
            } finally {
                setIsSearchingBoardSubSpeciality(false)
            }
        }
        searchBoardSubSpecialities()
    }, [debouncedBoardSubSpecialitySearch, activeSpecialityId])
    const isNpiRequired = (licenseTypeId: string): boolean => {
        const licenseType = licenseTypes.find(lt => lt._id === licenseTypeId)
        return licenseType?.required === 'mandatory'
    }

    // Fetch licenses
    const loadLicenses = useCallback(async () => {
        setIsLoadingLicenses(true)
        try {
            const response = await membershipApi.getLicensesAdmin(1, 10)
            const licensesData = (response as LicensesResponse).data || []
            // Filter out deleted licenses
            const activeLicenses = licensesData.filter(license => !license.isDeleted)
            setLicenses(activeLicenses)
        } catch (e) {
            console.error('Failed to load licenses', e)
            toast.error('Failed to load licenses')
        } finally {
            setIsLoadingLicenses(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => {
        loadLicenses()
    }, [loadLicenses])

    const handleSaveLicense = async (license: License) => {
        try {
            // Validate NPI if required
            if (license.licenseType !== '69aacb7782693bdedf50db43') {
                if (!license.licenseType) {
                    toast.error('License Type is required')
                    return
                }
                if (!license.licenseNumber) {
                    toast.error('License number is required')
                    return
                }
                if (!license.licenseState) {
                    toast.error('License State is required')
                    return
                }
                if (!license.licenseNumber) {
                    toast.error('License Number is required')
                    return
                }
                if (!license.issueDate) {
                    toast.error('Issue Date is required')
                    return
                }
                if (!license.expiryDate) {
                    toast.error('Expiry Date is required')
                    return
                }
                //expri date must be geter then issue date
                if (license.issueDate && license.expiryDate) {
                    const issue = new Date(license.issueDate)
                    const expiry = new Date(license.expiryDate)
                    if (expiry <= issue) {
                        toast.error('Expiry Date must be after Issue Date')
                        return
                    }
                }
                if (!license.licenseStatus) {
                    toast.error('License Status is required')
                    return
                }
                if (!license) {
                    toast.error('License File is required')
                    return
                }
            }




            const formData = new FormData()
            formData.append('licenseType', license.licenseType)
            formData.append('licenseNpi', license.licenseNpi || '')
            formData.append('licenseState', license.licenseState)
            formData.append('licenseNumber', license.licenseNumber)
            if (license.issueDate) {
                formData.append('issueDate', license.issueDate)
            }
            formData.append('expiryDate', license.expiryDate)
            formData.append('licenseStatus', license.licenseStatus)
            const getFieldValue = (field: any) => {
                if (field && typeof field === 'object') {
                    return field._id || field.name || '';
                }
                return field || '';
            };

            formData.append('boardName', getFieldValue(license.boardName))
            formData.append('boardSpeciality', getFieldValue(license.boardSpeciality))
            formData.append('boardSubSpeciality', getFieldValue(license.boardSubSpeciality))
            formData.append('boardCertificationNo', license.boardCertificationNo || '')

            // Include the files if any were selected for this license
            const filesToUpload = licenseFile.get(license._id)
            if (filesToUpload && filesToUpload.length > 0) {
                filesToUpload.forEach(file => {
                    formData.append('files', file)
                })
            }

            await membershipApi.updateLicenseAdmin(license._id, formData)
            toast.success('License updated successfully')

            // Clean up: remove the files from state
            const filesToCleanup = licenseFile.get(license._id)
            if (filesToCleanup) {
                if (typeof license.licenseFileUrl === 'string' && license.licenseFileUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(license.licenseFileUrl)
                }
            }
            setLicenseFile(prev => {
                const newMap = new Map(prev)
                newMap.delete(license._id)
                return newMap
            })

            setEditingLicenseId(null)

            // Reload licenses to get fresh data
            loadLicenses()
        } catch (e) {
            console.error('Failed to save license', e)
            toast.error('Failed to save license')
        }
    }

    const handleCancelEdit = () => {
        // Clean up any files that were selected but not saved
        const licenseId = editingLicenseId
        if (licenseId) {
            const license = licenses.find(lic => lic._id === licenseId)
            if (license) {
                // Check if we have files for this license that were just selected
                const files = licenseFile.get(licenseId)
                if (files) {
                    // Restore original URL if it exists (from previous save)
                    // For now, we'll just remove the file from state
                    setLicenseFile(prev => {
                        const newMap = new Map(prev)
                        newMap.delete(licenseId)
                        return newMap
                    })
                    // Reload to get original license data back
                    loadLicenses()
                }
            }
        }
        setEditingLicenseId(null)
    }

    const handleEditLicense = (licenseId: string) => {
        setEditingLicenseId(licenseId)
    }
    const handleDeleteLicenseClick = (license: License) => {
        setLicenseToDelete(license)
        setDeleteDialogOpen(true)
    }

    const handleLicenseFieldChange = (
        licenseId: string,
        field: keyof License,
        value: string
    ) => {
        setLicenses(prev =>
            prev.map(lic =>
                lic._id === licenseId ? { ...lic, [field]: value } : lic
            )
        )
    }

    const handleLicenseFileUpload = (licenseId: string, files: FileList | null) => {
        if (!files) return
        const newFiles = Array.from(files)
        
        setLicenseFile(prev => {
            const newMap = new Map(prev)
            const existingFiles = newMap.get(licenseId) || []
            
            if (existingFiles.length + newFiles.length > 5) {
                setTimeout(() => toast.error('Maximum 5 files allowed in total'), 0)
                const availableSpace = 5 - existingFiles.length
                if (availableSpace <= 0) return prev
                const updatedFiles = [...existingFiles, ...newFiles.slice(0, availableSpace)]
                newMap.set(licenseId, updatedFiles)
                setTimeout(() => toast.success(`${newFiles.slice(0, availableSpace).length} file(s) added`), 0)
            } else {
                const updatedFiles = [...existingFiles, ...newFiles]
                newMap.set(licenseId, updatedFiles)
                setTimeout(() => toast.success(`${newFiles.length} file(s) added. Total: ${updatedFiles.length}`), 0)
            }
            return newMap
        })
    }

    const handleNewLicenseChange = (field: string, value: string) => {
        setNewLicense(prev => ({ ...prev, [field]: value }))
    }

    const handleNewLicenseFileUpload = (files: FileList | null) => {
        if (!files) return
        const incomingFiles = Array.from(files)
        
        setNewLicenseFiles(prev => {
            if (prev.length + incomingFiles.length > 5) {
                setTimeout(() => toast.error('Maximum 5 files allowed in total'), 0)
                const availableSpace = 5 - prev.length
                if (availableSpace <= 0) return prev
                const updated = [...prev, ...incomingFiles.slice(0, availableSpace)]
                setTimeout(() => toast.success(`${incomingFiles.slice(0, availableSpace).length} file(s) added`), 0)
                return updated
            } else {
                const updated = [...prev, ...incomingFiles]
                setTimeout(() => toast.success(`${incomingFiles.length} file(s) added. Total: ${updated.length}`), 0)
                return updated
            }
        })
    }

    const handleAddLicense = async () => {

        try {
            setAddLoading(true)
            // Validate NPI if required
            if (newLicense.licenseType !== '69aacb7782693bdedf50db43') {
                if (
                    isNpiRequired(newLicense.licenseType) &&
                    !newLicense.licenseNpi?.trim()
                ) {
                    toast.error('NPI Number is required for this license type')
                    return
                }
                if (!newLicense.licenseState) {
                    toast.error('License State is required')
                    return
                }
                if (!newLicense.licenseNumber) {
                    toast.error('License Number is required')
                    return
                }
                if (!newLicense.issueDate) {
                    toast.error('Issue Date is required')
                    return
                }
                if (!newLicense.expiryDate) {
                    toast.error('Expiry Date is required')
                    return
                }
                //expri date must be geter then issue date
                if (newLicense.issueDate && newLicense.expiryDate) {
                    const issue = new Date(newLicense.issueDate)
                    const expiry = new Date(newLicense.expiryDate)
                    if (expiry <= issue) {
                        toast.error('Expiry Date must be after Issue Date')
                        return
                    }
                }
                if (!newLicense.licenseStatus) {
                    toast.error('License Status is required')
                    return
                }
                if (newLicenseFiles.length === 0) {
                    toast.error('License File is required')
                    return
                }
            }

            const formData = new FormData()
            formData.append('licenseType', newLicense.licenseType || '')
            formData.append('licenseNpi', newLicense.licenseNpi || '')
            formData.append('licenseState', newLicense.licenseState || '')
            formData.append('licenseNumber', newLicense.licenseNumber || '')
            if (newLicense.issueDate) {
                formData.append('issueDate', newLicense.issueDate)
            }
            formData.append('expiryDate', newLicense.expiryDate || '')
            formData.append('licenseStatus', newLicense.licenseStatus || '')
            const getFieldValue = (field: any) => {
                if (field && typeof field === 'object') {
                    return field._id || field.name || '';
                }
                return field || '';
            };

            formData.append('boardName', getFieldValue(newLicense.boardName))
            formData.append('boardSpeciality', getFieldValue(newLicense.boardSpeciality))
            formData.append('boardSubSpeciality', getFieldValue(newLicense.boardSubSpeciality))
            formData.append('boardCertificationNo', newLicense.boardCertificationNo || '')

            // Include the files if any were selected
            if (newLicenseFiles.length > 0) {
                newLicenseFiles.forEach(file => {
                    formData.append('files', file)
                })
            }

            await membershipApi.addLicenseCertificate(formData)
            toast.success('License added successfully')
            setAddLoading(false)
            handleCloseAddLicenseDialog()
            // Reload licenses to get fresh data
            loadLicenses()
        } catch (e) {
            setAddLoading(false)
            console.error('Failed to add license', e)
            toast.error('Failed to add license')
        }
    }

    useEffect(() => {
        const loadStates = async () => {
            try {
                const response = await membershipApi.getStates()
                const statesData = response.data || response || []
                setStates(statesData)
            } catch (e) {
                console.error('Failed to load states', e)
                toast.error('Failed to load states')
            }
        }
        loadStates()

        const loadLicenseTypes = async () => {
            try {
                setIsLoadingLicenseTypes(true)
                const response = await membershipApi.getSpecialtyRoles()
                const licenseTypesData = response.data || response || []
                if (Array.isArray(licenseTypesData)) {
                    setLicenseTypes(
                        licenseTypesData.filter((lt: LicenseType) => !lt.isDeleted)
                    )
                }
            } catch (e) {
                console.error('Failed to load license types', e)
            } finally {
                setIsLoadingLicenseTypes(false)
            }
        }
        loadLicenseTypes()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleConfirmDelete = async () => {
        if (!licenseToDelete) return
        try {
            await membershipApi.deleteLicenseAdmin(licenseToDelete._id)
            toast.success('License deleted successfully')
            setDeleteDialogOpen(false)
            setLicenseToDelete(null)
            // Reload licenses
            loadLicenses()
        } catch (e) {
            console.error('Failed to delete license', e)
            toast.error('Failed to delete license')
        }
    }

    return (
        <Grid item xs={12}>
            <Card>
                <CardContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: 'grey.800',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <BadgeIcon sx={{ color: 'primary.main' }} />
                            License Information
                        </Typography>
                        {licenses.length === 0 && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAddLicenseDialog}
                                sx={{ textTransform: 'none' }}
                            >
                                {addLoading ? <Loader /> : "Add License"}
                            </Button>
                        )}
                    </Box>

                    {isLoadingLicenses ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : licenses.length === 0 ? (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 4,
                                color: 'grey.500',
                            }}
                        >
                            <Typography variant="body1">
                                No licenses found. Please add your license information.
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {licenses.map(license => {
                                const isEditing = editingLicenseId === license._id
                                return (
                                    <Grid item xs={12} key={license._id}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                border: '1px solid',
                                                borderColor: 'grey.200',
                                                borderRadius: 2,
                                                bgcolor: 'grey.50',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    mb: 3,
                                                }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: 'grey.800',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        {/* {license.licenseName || license.licenseType} */}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {isEditing ? (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<SaveIcon />}
                                                                onClick={() => handleSaveLicense(license)}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={handleCancelEdit}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<EditIcon />}
                                                                onClick={() =>
                                                                    handleEditLicense(license._id)
                                                                }
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="error"
                                                                startIcon={<DeleteIcon fontSize="small" />}
                                                                onClick={() =>
                                                                    handleDeleteLicenseClick(license)
                                                                }
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>

                                            <Grid container spacing={2}>
                                                {!isEditing && (
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            label="Certifier's Name"
                                                            value={user?.firstName || ""}
                                                            InputProps={{
                                                                readOnly: true,
                                                            }}
                                                            variant="outlined"
                                                            sx={{
                                                                '& .MuiInputBase-input.Mui-readOnly': {
                                                                    color: 'grey.600',
                                                                    WebkitTextFillColor: 'grey.600',
                                                                },
                                                            }}
                                                        />
                                                    </Grid>
                                                )}


                                                {/* 2) Right - License Status */}
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl fullWidth disabled={!isEditing}>
                                                        <InputLabel>License Status *</InputLabel>
                                                        <Select
                                                            value={license.licenseStatus}
                                                            onChange={e =>
                                                                handleLicenseFieldChange(
                                                                    license._id,
                                                                    'licenseStatus',
                                                                    e.target.value
                                                                )
                                                            }
                                                            label="License Status *"
                                                        >
                                                            <MenuItem value="Active">Active</MenuItem>
                                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                                            <MenuItem value="Suspended">Suspended</MenuItem>
                                                            <MenuItem value="Expired">Expired</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                {/* 3) Left - Specialty License Type */}
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl
                                                        fullWidth
                                                        disabled={!isEditing || isLoadingLicenseTypes}
                                                    >
                                                        <InputLabel>
                                                            Specialty License Type *
                                                        </InputLabel>
                                                        <Select
                                                            value={license.licenseType}
                                                            onChange={e =>
                                                                handleLicenseFieldChange(
                                                                    license._id,
                                                                    'licenseType',
                                                                    e.target.value
                                                                )
                                                            }
                                                            label="Specialty License Type *"
                                                        >
                                                            {isLoadingLicenseTypes ? (
                                                                <MenuItem disabled>Loading...</MenuItem>
                                                            ) : (
                                                                licenseTypes.map(licenseType => (
                                                                    <MenuItem
                                                                        key={licenseType._id}
                                                                        value={licenseType._id}
                                                                    >
                                                                        {licenseType.task}
                                                                    </MenuItem>
                                                                ))
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                {/* 4) Right - NPI Number */}
                                                <Grid item xs={12} sm={6}>
                                                    {license.licenseType !== '69aacb7782693bdedf50db43' && (
                                                        <TextField
                                                            label="NPI Number"
                                                            value={license.licenseNpi || ''}
                                                            onChange={e =>
                                                                handleLicenseFieldChange(
                                                                    license._id,
                                                                    'licenseNpi',
                                                                    e.target.value
                                                                )
                                                            }
                                                            fullWidth
                                                            disabled={!isEditing}
                                                            required={isNpiRequired(license.licenseType)}
                                                            error={
                                                                isEditing &&
                                                                isNpiRequired(license.licenseType) &&
                                                                !license.licenseNpi?.trim()
                                                            }
                                                            helperText={
                                                                isEditing &&
                                                                    isNpiRequired(license.licenseType) &&
                                                                    !license.licenseNpi?.trim()
                                                                    ? 'NPI Number is required for this license type'
                                                                    : ''
                                                            }
                                                        />
                                                    )}
                                                </Grid>

                                                {/* 5) Left - License State */}
                                                <Grid item xs={12} sm={6}>
                                                    {license.licenseType !== '69aacb7782693bdedf50db43' && (
                                                        <FormControl fullWidth disabled={!isEditing}>
                                                            <InputLabel>License State *</InputLabel>
                                                            <Select
                                                                value={license.licenseState}
                                                                onChange={e =>
                                                                    handleLicenseFieldChange(
                                                                        license._id,
                                                                        'licenseState',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                label="License State *"
                                                            >
                                                                {states.map(state => (
                                                                    <MenuItem
                                                                        key={state.code || state.name}
                                                                        value={state.name}
                                                                    >
                                                                        {state.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Grid>
                                                {/* 12) Board Certification No */}
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Board Certification No"
                                                        value={license.boardCertificationNo || ''}
                                                        onChange={e =>
                                                            handleLicenseFieldChange(
                                                                license._id,
                                                                'boardCertificationNo',
                                                                e.target.value
                                                            )
                                                        }
                                                        fullWidth
                                                        disabled={!isEditing}
                                                    />
                                                </Grid>

                                                {/* 6) Right - Board */}
                                                <Grid item xs={12} sm={6}>
                                                    <Autocomplete
                                                        disabled={!isEditing}
                                                        options={boardNameOptions}
                                                        value={(() => {
                                                            const val = license.boardName;
                                                            if (!val) return null;
                                                            const id = typeof val === 'string' ? val : val._id;
                                                            const name = typeof val === 'string' ? val : val.name;
                                                            return boardNameOptions.find(o => o._id === id) || { _id: id, name: name };
                                                        })()}
                                                        inputValue={boardNameSearch}
                                                        onInputChange={(_, newValue) => setBoardNameSearch(newValue)}
                                                        onChange={(_, newValue) => {
                                                            handleLicenseFieldChange(license._id, 'boardName', newValue?._id || '')
                                                            handleLicenseFieldChange(license._id, 'boardSpeciality', '')
                                                            handleLicenseFieldChange(license._id, 'boardSubSpeciality', '')
                                                            setBoardSpecialitySearch('')
                                                            setBoardSubSpecialitySearch('')
                                                        }}
                                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                                        loading={isSearchingBoardName}
                                                        filterOptions={x => x}
                                                        openOnFocus
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Board"
                                                                disabled={!isEditing}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {isSearchingBoardName ? <CircularProgress color="inherit" size={20} /> : null}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>

                                                {/* 7) Left - Board Specialty */}
                                                <Grid item xs={12} sm={6}>
                                                    <Autocomplete
                                                        disabled={!isEditing || !license.boardName}
                                                        options={boardSpecialityOptions}
                                                        value={(() => {
                                                            const val = license.boardSpeciality;
                                                            if (!val) return null;
                                                            const id = typeof val === 'string' ? val : val._id;
                                                            const name = typeof val === 'string' ? val : val.name;
                                                            return boardSpecialityOptions.find(o => o._id === id) || { _id: id, name: name || 'Loading...' };
                                                        })()}
                                                        inputValue={boardSpecialitySearch}
                                                        onInputChange={(_, newValue) => setBoardSpecialitySearch(newValue)}
                                                        onChange={(_, newValue) => {
                                                            handleLicenseFieldChange(license._id, 'boardSpeciality', newValue?._id || '')
                                                            handleLicenseFieldChange(license._id, 'boardSubSpeciality', '')
                                                            setBoardSubSpecialitySearch('')
                                                        }}
                                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                                        loading={isSearchingBoardSpeciality}
                                                        filterOptions={x => x}
                                                        openOnFocus
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Board Specialty"
                                                                disabled={!isEditing || !license.boardName}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {isSearchingBoardSpeciality ? <CircularProgress color="inherit" size={20} /> : null}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>

                                                {/* 8) Right - Board Sub-Specialty */}
                                                <Grid item xs={12} sm={6}>
                                                    <Autocomplete
                                                        disabled={!isEditing || !license.boardSpeciality}
                                                        options={boardSubSpecialityOptions}
                                                        value={(() => {
                                                            const val = license.boardSubSpeciality;
                                                            if (!val) return null;
                                                            const id = typeof val === 'string' ? val : val._id;
                                                            const name = typeof val === 'string' ? val : val.name;
                                                            return boardSubSpecialityOptions.find(o => o._id === id) || { _id: id, name: name || 'Loading...' };
                                                        })()}
                                                        inputValue={boardSubSpecialitySearch}
                                                        onInputChange={(_, newValue) => setBoardSubSpecialitySearch(newValue)}
                                                        onChange={(_, newValue) => {
                                                            handleLicenseFieldChange(license._id, 'boardSubSpeciality', newValue?._id || '')
                                                        }}
                                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                                        loading={isSearchingBoardSubSpeciality}
                                                        filterOptions={x => x}
                                                        openOnFocus
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Board Sub-Specialty"
                                                                disabled={!isEditing || !license.boardSpeciality}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {isSearchingBoardSubSpeciality ? <CircularProgress color="inherit" size={20} /> : null}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>

                                                {/* 9) Left - License Number */}
                                                <Grid item xs={12} sm={6}>
                                                    {license.licenseType !== '69aacb7782693bdedf50db43' && (
                                                        <TextField
                                                            label="License Number *"
                                                            value={license.licenseNumber}
                                                            onChange={e =>
                                                                handleLicenseFieldChange(
                                                                    license._id,
                                                                    'licenseNumber',
                                                                    e.target.value
                                                                )
                                                            }
                                                            fullWidth
                                                            disabled={!isEditing}
                                                        />
                                                    )}
                                                </Grid>

                                                {/* 10) Right - License Issue Date */}
                                                <Grid item xs={12} sm={6}>
                                                    {license.licenseType !== '69aacb7782693bdedf50db43' && (
                                                        <LocalizationProvider
                                                            dateAdapter={AdapterDateFns}
                                                        >
                                                            <DatePicker
                                                                label="License Issue Date *"
                                                                value={
                                                                    license.issueDate
                                                                        ? new Date(license.issueDate)
                                                                        : null
                                                                }
                                                                onChange={value => {
                                                                    const formatted = value
                                                                        ? formatDateFns(
                                                                            value as Date,
                                                                            'MM/dd/yyyy'
                                                                        )
                                                                        : ''
                                                                    handleLicenseFieldChange(
                                                                        license._id,
                                                                        'issueDate',
                                                                        formatted
                                                                    )
                                                                }}
                                                                format="MM/dd/yyyy"
                                                                views={['year', 'month', 'day']}
                                                                openTo="year"
                                                                maxDate={new Date()}
                                                                disabled={!isEditing}
                                                                slotProps={{
                                                                    textField: {
                                                                        fullWidth: true,
                                                                        placeholder: 'MM/DD/YYYY',
                                                                    },
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    )}
                                                </Grid>

                                                {/* 11) Left - License Expiration Date */}
                                                <Grid item xs={12} sm={6}>
                                                    {license.licenseType !== '69aacb7782693bdedf50db43' && (
                                                        <LocalizationProvider
                                                            dateAdapter={AdapterDateFns}
                                                        >
                                                            <DatePicker
                                                                label="License Expiration Date *"
                                                                value={
                                                                    license.expiryDate
                                                                        ? new Date(license.expiryDate)
                                                                        : null
                                                                }
                                                                onChange={value => {
                                                                    const formatted = value
                                                                        ? formatDateFns(
                                                                            value as Date,
                                                                            'MM/dd/yyyy'
                                                                        )
                                                                        : ''
                                                                    handleLicenseFieldChange(
                                                                        license._id,
                                                                        'expiryDate',
                                                                        formatted
                                                                    )
                                                                }}
                                                                format="MM/dd/yyyy"
                                                                views={['year', 'month', 'day']}
                                                                openTo="year"
                                                                disabled={!isEditing}
                                                                slotProps={{
                                                                    textField: {
                                                                        fullWidth: true,
                                                                        placeholder: 'MM/DD/YYYY',
                                                                    },
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    )}
                                                </Grid>



                                                {/* Upload License Document */}
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ mb: 1, fontWeight: 500 }}
                                                    >
                                                        Upload License Document {license.licenseType !== '69aacb7782693bdedf50db43' ? '*' : ''}
                                                    </Typography>
                                                    {license.licenseFileUrl ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexWrap: 'wrap',
                                                                    gap: 2,
                                                                }}
                                                            >
                                                                {(() => {
                                                                    // Consolidate all possible file sources
                                                                    let urls: string[] = [];

                                                                    if (Array.isArray(license.licenseFileUrls) && license.licenseFileUrls.length > 0) {
                                                                        urls = license.licenseFileUrls;
                                                                    } else if (Array.isArray(license.file) && license.file.length > 0) {
                                                                        urls = license.file.map(f => typeof f === 'string' ? f : (f.signedUrl || f.url || f.uri || ''));
                                                                    } else if (Array.isArray(license.licenseFileUrl)) {
                                                                        urls = license.licenseFileUrl;
                                                                    } else {
                                                                        // Check numbered fields specifically
                                                                        if (typeof license.licenseFileUrl === 'string' && license.licenseFileUrl) urls.push(license.licenseFileUrl);
                                                                        if (license.licenseFileUrl1) urls.push(license.licenseFileUrl1);
                                                                        if (license.licenseFileUrl2) urls.push(license.licenseFileUrl2);
                                                                        if (license.licenseFileUrl3) urls.push(license.licenseFileUrl3);
                                                                        if (license.licenseFileUrl4) urls.push(license.licenseFileUrl4);
                                                                    }

                                                                    if (urls.length > 1) {
                                                                        return urls.map((url, idx) => (
                                                                            <Button
                                                                                key={idx}
                                                                                variant="outlined"
                                                                                size="small"
                                                                                href={url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                startIcon={<VisibilityIcon />}
                                                                                sx={{ textTransform: 'none' }}
                                                                            >
                                                                                Document {idx + 1}
                                                                            </Button>
                                                                        ));
                                                                    } else if (urls.length === 1) {
                                                                        return (
                                                                            <Button
                                                                                variant="outlined"
                                                                                size="small"
                                                                                href={urls[0]}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                startIcon={<VisibilityIcon />}
                                                                                sx={{ textTransform: 'none' }}
                                                                            >
                                                                                View Document
                                                                            </Button>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })()}

                                                                {isEditing && (
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        component="label"
                                                                        startIcon={<CloudUploadIcon />}
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        {license.licenseFileUrl ? 'Upload Documents' : 'Upload Documents'}
                                                                        <input
                                                                            type="file"
                                                                            multiple
                                                                            hidden
                                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                                                    onChange={e => {
                                                                                        handleLicenseFileUpload(
                                                                                            license._id,
                                                                                            e.target.files
                                                                                        )
                                                                                        e.target.value = ''
                                                                                    }}
                                                                        />
                                                                    </Button>
                                                                )}
                                                            </Box>

                                                            {isEditing && licenseFile.has(license._id) && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                                                        Selected for upload:
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {licenseFile.get(license._id)?.map((file, idx) => (
                                                                            <Chip
                                                                                key={idx}
                                                                                label={file.name}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onDelete={() => {
                                                                                    const currentFiles = licenseFile.get(license._id) || []
                                                                                    const updatedFiles = currentFiles.filter((_, i) => i !== idx)
                                                                                    setLicenseFile(prev => {
                                                                                        const newMap = new Map(prev)
                                                                                        if (updatedFiles.length > 0) {
                                                                                            newMap.set(license._id, updatedFiles)
                                                                                        } else {
                                                                                            newMap.delete(license._id)
                                                                                        }
                                                                                        return newMap
                                                                                    })
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                border: '2px dashed',
                                                                borderColor: 'grey.300',
                                                                borderRadius: 1,
                                                                p: 3,
                                                                textAlign: 'center',
                                                                bgcolor: 'background.paper',
                                                            }}
                                                        >
                                                            <CloudUploadIcon
                                                                sx={{
                                                                    fontSize: 48,
                                                                    color: 'primary.main',
                                                                    mb: 1,
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                Drag and drop your license document, or
                                                                click to browse
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: 'block', mt: 0.5 }}
                                                            >
                                                                Supported formats: PDF, JPG, PNG (Max 10MB)
                                                            </Typography>
                                                            {isEditing && (
                                                                <Button
                                                                    component="label"
                                                                    variant="text"
                                                                    sx={{ mt: 1, textTransform: 'none' }}
                                                                >
                                                                    browse
                                                                    <input
                                                                        type="file"
                                                                        multiple
                                                                        hidden
                                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                                                onChange={e => {
                                                                                    handleLicenseFileUpload(
                                                                                        license._id,
                                                                                        e.target.files
                                                                                    )
                                                                                    e.target.value = ''
                                                                                }}
                                                                    />
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                )
                            })}
                        </Grid>
                    )}
                </CardContent>
            </Card>
            <Dialog
                open={addLicenseDialogOpen}
                onClose={handleCloseAddLicenseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Add New License
                        </Typography>
                        <IconButton onClick={handleCloseAddLicenseDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Grid container spacing={3}>


                        {/* 2) Right - License Status */}
                        <Grid item xs={12} sm={6}>
                            <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                            >
                                License Status *
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={newLicense.licenseStatus || 'Active'}
                                    onChange={e =>
                                        handleNewLicenseChange('licenseStatus', e.target.value)
                                    }
                                    sx={{
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'grey.300',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'grey.400',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#2E7DAF',
                                            borderWidth: 2,
                                        },
                                    }}
                                >
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                    <MenuItem value="Suspended">Suspended</MenuItem>
                                    <MenuItem value="Expired">Expired</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* 3) Left - Specialty License Type */}
                        <Grid item xs={12} sm={6}>
                            <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                            >
                                Specialty License Type *
                            </Typography>
                            <FormControl fullWidth disabled={isLoadingLicenseTypes}>
                                <Select
                                    value={newLicense.licenseType}
                                    onChange={e =>
                                        handleNewLicenseChange('licenseType', e.target.value)
                                    }
                                    displayEmpty
                                    sx={{
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'grey.300',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'grey.400',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#2E7DAF',
                                            borderWidth: 2,
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <em>
                                            {isLoadingLicenseTypes
                                                ? 'Loading license types...'
                                                : 'Select license type'}
                                        </em>
                                    </MenuItem>
                                    {licenseTypes.map(licenseType => (
                                        <MenuItem key={licenseType._id} value={licenseType._id}>
                                            {licenseType.task}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* 4) Right - NPI Number */}
                        <Grid item xs={12} sm={6}>
                            {newLicense.licenseType !== '69aacb7782693bdedf50db43' && (
                                <>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        NPI Number{isNpiRequired(newLicense.licenseType) ? ' *' : ''}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter NPI number"
                                        value={newLicense.licenseNpi || ''}
                                        onChange={e =>
                                            handleNewLicenseChange('licenseNpi', e.target.value)
                                        }
                                        required={isNpiRequired(newLicense.licenseType)}
                                        error={
                                            isNpiRequired(newLicense.licenseType) &&
                                            !newLicense.licenseNpi?.trim()
                                        }
                                        helperText={
                                            isNpiRequired(newLicense.licenseType) &&
                                                !newLicense.licenseNpi?.trim()
                                                ? 'NPI Number is required for this license type'
                                                : ''
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'white',
                                                borderRadius: 2,
                                                '& fieldset': {
                                                    borderColor: 'grey.300',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey.400',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#2E7DAF',
                                                    borderWidth: 2,
                                                },
                                            },
                                        }}
                                    />
                                </>
                            )}
                        </Grid>

                        {/* 5) Left - License State */}
                        <Grid item xs={12} sm={6}>
                            {newLicense.licenseType !== '69aacb7782693bdedf50db43' && (
                                <>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License State *
                                    </Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            value={newLicense.licenseState}
                                            onChange={e =>
                                                handleNewLicenseChange('licenseState', e.target.value)
                                            }
                                            displayEmpty
                                            sx={{
                                                borderRadius: 2,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'grey.300',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'grey.400',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2E7DAF',
                                                    borderWidth: 2,
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em>Select state</em>
                                            </MenuItem>
                                            {states.map(state => (
                                                <MenuItem
                                                    key={state.code || state.name}
                                                    value={state.name}
                                                >
                                                    {state.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </>
                            )}
                        </Grid>

                        {/* 12) [Additional] Board Certification No */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}>
                                Board Certification No
                            </Typography>
                            <TextField
                                placeholder="Enter board certification number"
                                value={newLicense.boardCertificationNo || ''}
                                onChange={e => handleNewLicenseChange('boardCertificationNo', e.target.value)}
                                fullWidth
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                            />
                        </Grid>

                        {/* 6) Right - Board */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}>
                                Board
                            </Typography>
                            <Autocomplete
                                options={boardNameOptions}
                                value={boardNameOptions.find(o => o._id === newLicense.boardName) || null}
                                onInputChange={(_, newValue) => setBoardNameSearch(newValue)}
                                onChange={(_, newValue) => {
                                    setNewLicense(prev => ({
                                        ...prev,
                                        boardName: newValue?._id || '',
                                        boardSpeciality: '',
                                        boardSubSpeciality: '',
                                    }))
                                    setBoardSpecialitySearch('')
                                    setBoardSubSpecialitySearch('')
                                }}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                loading={isSearchingBoardName}
                                filterOptions={x => x}
                                openOnFocus
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search and select board"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isSearchingBoardName ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* 7) Left - Board Specialty */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}>
                                Board Specialty
                            </Typography>
                            <Autocomplete
                                disabled={!newLicense.boardName}
                                options={boardSpecialityOptions}
                                value={boardSpecialityOptions.find(o => o._id === newLicense.boardSpeciality) || null}
                                onInputChange={(_, newValue) => setBoardSpecialitySearch(newValue)}
                                onChange={(_, newValue) => {
                                    setNewLicense(prev => ({
                                        ...prev,
                                        boardSpeciality: newValue?._id || '',
                                        boardSubSpeciality: '',
                                    }))
                                    setBoardSubSpecialitySearch('')
                                }}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                loading={isSearchingBoardSpeciality}
                                filterOptions={x => x}
                                openOnFocus
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search and select speciality"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isSearchingBoardSpeciality ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* 8) Right - Board Sub-Specialty */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}>
                                Board Sub-Specialty
                            </Typography>
                            <Autocomplete
                                disabled={!newLicense.boardSpeciality}
                                options={boardSubSpecialityOptions}
                                value={boardSubSpecialityOptions.find(o => o._id === newLicense.boardSubSpeciality) || null}
                                onInputChange={(_, newValue) => setBoardSubSpecialitySearch(newValue)}
                                onChange={(_, newValue) => {
                                    setNewLicense(prev => ({
                                        ...prev,
                                        boardSubSpeciality: newValue?._id || '',
                                    }))
                                }}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                loading={isSearchingBoardSubSpeciality}
                                filterOptions={x => x}
                                openOnFocus
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search and select sub-speciality"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isSearchingBoardSubSpeciality ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* 9) Left - License Number */}
                        <Grid item xs={12} sm={6}>
                            {newLicense.licenseType !== '69aacb7782693bdedf50db43' && (
                                <>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License Number *
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter license number"
                                        value={newLicense.licenseNumber}
                                        onChange={e =>
                                            handleNewLicenseChange('licenseNumber', e.target.value)
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'white',
                                                borderRadius: 2,
                                                '& fieldset': {
                                                    borderColor: 'grey.300',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey.400',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#2E7DAF',
                                                    borderWidth: 2,
                                                },
                                            },
                                        }}
                                    />
                                </>
                            )}
                        </Grid>

                        {/* 10) Right - License Issued Date */}
                        <Grid item xs={12} sm={6}>
                            {newLicense.licenseType !== '69aacb7782693bdedf50db43' && (
                                <>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License Issue Date *
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            value={
                                                newLicense.issueDate
                                                    ? new Date(newLicense.issueDate)
                                                    : null
                                            }
                                            onChange={value => {
                                                const formatted = value
                                                    ? formatDateFns(value as Date, 'MM/dd/yyyy')
                                                    : ''
                                                handleNewLicenseChange('issueDate', formatted)
                                            }}
                                            format="MM/dd/yyyy"
                                            views={['year', 'month', 'day']}
                                            openTo="year"
                                            maxDate={new Date()}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    placeholder: 'MM/DD/YYYY',
                                                    sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: 'white',
                                                            borderRadius: 2,
                                                            '& fieldset': {
                                                                borderColor: 'grey.300',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'grey.400',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#2E7DAF',
                                                                borderWidth: 2,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </>
                            )}
                        </Grid>

                        {/* 11) Left - License Expiration Date */}
                        <Grid item xs={12} sm={6}>
                            {newLicense.licenseType !== '69aacb7782693bdedf50db43' && (
                                <>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License Expiration Date *
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            value={
                                                newLicense.expiryDate
                                                    ? new Date(newLicense.expiryDate)
                                                    : null
                                            }
                                            onChange={value => {
                                                const formatted = value
                                                    ? formatDateFns(value as Date, 'MM/dd/yyyy')
                                                    : ''
                                                handleNewLicenseChange('expiryDate', formatted)
                                            }}
                                            format="MM/dd/yyyy"
                                            views={['year', 'month', 'day']}
                                            openTo="year"
                                            minDate={new Date()}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    placeholder: 'MM/DD/YYYY',
                                                    sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: 'white',
                                                            borderRadius: 2,
                                                            '& fieldset': {
                                                                borderColor: 'grey.300',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'grey.400',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#2E7DAF',
                                                                borderWidth: 2,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </>
                            )}
                        </Grid>


                        {/* Upload License Document */}
                        <Grid item xs={12}>
                            <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                            >
                                Upload License Document {newLicense.licenseType !== '69aacb7782693bdedf50db43' ? '*' : ''}
                            </Typography>
                            <Box
                                sx={{
                                    border: '2px dashed #d1d5db',
                                    borderRadius: 2,
                                    p: 4,
                                    textAlign: 'center',
                                    bgcolor: 'white',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        borderColor: '#2E7DAF',
                                        bgcolor: '#f8f9ff',
                                    },
                                }}
                                onClick={() =>
                                    document.getElementById('new-license-upload')?.click()
                                }
                            >
                                <CloudUploadIcon
                                    sx={{
                                        fontSize: 48,
                                        color: '#9ca3af',
                                        mb: 2,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'grey.600',
                                        mb: 1,
                                        fontWeight: 500,
                                    }}
                                >
                                    Drag and drop your license document, or click to browse
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'grey.500',
                                        display: 'block',
                                        mb: 2,
                                    }}
                                >
                                    Supported formats: PDF, JPG, PNG (Max 10MB)
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#2E7DAF',
                                        fontWeight: 500,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                >
                                    browse
                                </Typography>
                                <input
                                    id="new-license-upload"
                                    type="file"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    style={{ display: 'none' }}
                                    onChange={e => {
                                        handleNewLicenseFileUpload(e.target.files)
                                        e.target.value = ''
                                    }}
                                />
                                {newLicenseFiles.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                                            Selected Documents ({newLicenseFiles.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                            {newLicenseFiles.map((file, index) => (
                                                <Paper
                                                    key={index}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        borderRadius: 2,
                                                        bgcolor: 'grey.50'
                                                    }}
                                                >
                                                    <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                                                        {file.name}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            setNewLicenseFiles(prev => prev.filter((_, i) => i !== index))
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Paper>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseAddLicenseDialog}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddLicense}
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                    >
                        {addLoading ? "Loading..." : "Add License"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Delete License
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete the license? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        sx={{ textTransform: 'none' }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}

export default LicenseManagementPage